import React, {useContext, useMemo} from "react";
import {Box, Button, Tooltip, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {Link as RouterLink, useParams} from "react-router-dom";
import {useQuery} from "react-query";

import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import CalculationTabs from "../../../components/CalculationTabs";
import {getOrder} from "../../../api/orderApi";
import {LanguageContext} from "../../../components/LanguageContext";

const Num = ({value, suffix}) => (
    <Typography variant="body2" sx={{fontFamily: "monospace", fontWeight: 600}}>
        {value === null || value === undefined || value === ""
            ? "—"
            : `${parseFloat(value).toFixed(0)}${suffix ? ` ${suffix}` : ""}`}
    </Typography>
);

const AcquisitionPage = () => {
    const {storeId, excelId} = useParams();
    const {translations} = useContext(LanguageContext) || {};

    const {data, isLoading} = useQuery({
        queryKey: ["orderGet", excelId],
        queryFn: () => getOrder(excelId),
    });

    const columns = useMemo(
        () => [
            {
                field: "order_id",
                headerName:
                    translations?.adminAcquisitionPageColumnID || "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{fontFamily: "monospace"}}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "supplier_name",
                headerName:
                    translations?.poslovodjaProizvodiSupplierName ||
                    "Dobavljač",
                minWidth: 180,
                flex: 1,
                renderCell: (params) => (
                    <Tooltip
                        arrow
                        placement="right"
                        title={
                            <Box>
                                <Typography variant="caption" component="div">
                                    <strong>
                                        {translations?.frequency || "Frekvencija"}:
                                    </strong>{" "}
                                    {params.row.frequency || "—"}
                                </Typography>
                                <Typography variant="caption" component="div">
                                    <strong>
                                        {translations?.orderDay || "Dan"}:
                                    </strong>{" "}
                                    {params.row.order_day || "—"}
                                </Typography>
                            </Box>
                        }
                    >
                        <span>{params.row.supplier_name}</span>
                    </Tooltip>
                ),
            },
            {
                field: "code",
                headerName:
                    translations?.adminAcquisitionPageColumnProductCode ||
                    "Šifra",
                minWidth: 140,
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        sx={{fontFamily: "monospace", fontWeight: 600}}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "name",
                headerName:
                    translations?.adminAcquisitionPageColumnProductName ||
                    "Naziv",
                minWidth: 240,
                flex: 2,
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={params.value}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "transport_package",
                headerName:
                    translations?.poslovodjaAcquisitionPageColumnTransportPackage ||
                    "Tr. pakovanje",
                minWidth: 160,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Num
                        value={params.row.transport_package}
                        suffix={translations?.pieces || "kom"}
                    />
                ),
            },
            {
                field: "needed",
                headerName:
                    translations?.adminAcquisitionPageColumnNeeded ||
                    "Potrebno",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => <Num value={params.row.needed}/>,
            },
            {
                field: "amount_on_storage",
                headerName:
                    translations?.adminAcquisitionPageColumnOnStock ||
                    "Na lageru",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Num value={params.row.amount_on_storage}/>
                ),
            },
            {
                field: "amount_on_order",
                headerName: translations?.onOrder || "Na narudžbi",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Num value={params.row.amount_on_order}/>
                ),
            },
            {
                field: "rop",
                headerName:
                    translations?.adminAcquisitionPageColumnOrderLevel ||
                    "ROP",
                minWidth: 110,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => <Num value={params.row.rop}/>,
            },
            {
                field: "amount_to_get",
                headerName:
                    translations?.adminAcquisitionPageColumnToAcquire ||
                    "Za nabavku",
                minWidth: 150,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    const v = parseFloat(params.row.amount_to_get) || 0;
                    const orderable = v > 0;
                    const tone = orderable ? "success" : "error";
                    const Icon = orderable
                        ? CheckCircleRoundedIcon
                        : RemoveCircleOutlineRoundedIcon;
                    return (
                        <Box
                            sx={(theme) => ({
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 999,
                                bgcolor: alpha(theme.palette[tone].main, 0.12),
                                color: theme.palette[tone].main,
                            })}
                        >
                            <Icon sx={{fontSize: "1rem"}}/>
                            <Typography
                                sx={{
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    fontSize: "0.85rem",
                                }}
                            >
                                {v.toFixed(0)}
                            </Typography>
                        </Box>
                    );
                },
            },
        ],
        [translations]
    );

    const rows = useMemo(() => data?.data?.orders || [], [data]);

    return (
        <Box>
            <PageHeader
                icon={<Inventory2OutlinedIcon/>}
                eyebrow={
                    translations?.adminExcelProInfoPageAcquisition || "Nabavka"
                }
                title={
                    translations?.adminAcquisitionPageTitle || "Nabavka"
                }
                description={
                    translations?.adminAcquisitionPageSubtitle ||
                    "Predlog količina po proizvodu i dobavljaču."
                }
                actions={
                    <Button
                        component={RouterLink}
                        to={`/prodavnica/${storeId}/informacije/${excelId}/narudzbine`}
                        variant="outlined"
                        startIcon={<FolderOpenOutlinedIcon/>}
                    >
                        {translations?.adminAcquisitionPageOrders ||
                            "Narudžbine"}
                    </Button>
                }
            />

            <CalculationTabs active="nabavka"/>

            <SectionCard padded={false}>
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    rowHeight={56}
                    emptyIcon={<Inventory2OutlinedIcon/>}
                    emptyTitle={
                        translations?.messageIfEmptyData ||
                        "Nema podataka za prikaz"
                    }
                />
            </SectionCard>
        </Box>
    );
};

export default AcquisitionPage;